// TinyMCE core and plugins
import { getThemeSwatches } from "./theme_colors"
import "../tinymce/tinymce.min"

/* Models, Icons, Theme */
import "../tinymce/models/dom/model.min"
import "../tinymce/icons/default/icons.min"
import "../tinymce/themes/silver/theme.min"

/* Plugins - Clean List Matching Your Path Structure */
// import "../tinymce/plugins/accordion/plugin.min"
import "../tinymce/plugins/advlist/plugin.min"
import "../tinymce/plugins/anchor/plugin.min"
import "../tinymce/plugins/autolink/plugin.min"
// import "../tinymce/plugins/autoresize/plugin.min" // Disabled to allow manual resize
import "../tinymce/plugins/autosave/plugin.min"
import "../tinymce/plugins/charmap/plugin.min"
import "../tinymce/plugins/code/plugin.min"
import "../tinymce/plugins/codesample/plugin.min"
import "../tinymce/plugins/directionality/plugin.min"
import "../tinymce/plugins/emoticons/plugin.min"
import "../tinymce/plugins/emoticons/js/emojis.min"
import "../tinymce/plugins/fullscreen/plugin.min"
import "../tinymce/plugins/help/plugin.min"
import "../tinymce/plugins/image/plugin.min"
import "../tinymce/plugins/importcss/plugin.min"
import "../tinymce/plugins/insertdatetime/plugin.min"
import "../tinymce/plugins/link/plugin.min"
import "../tinymce/plugins/lists/plugin.min"
import "../tinymce/plugins/media/plugin.min"
import "../tinymce/plugins/nonbreaking/plugin.min"
import "../tinymce/plugins/pagebreak/plugin.min"
import "../tinymce/plugins/preview/plugin.min"
import "../tinymce/plugins/quickbars/plugin.min"
import "../tinymce/plugins/save/plugin.min"
import "../tinymce/plugins/searchreplace/plugin.min"
import "../tinymce/plugins/table/plugin.min"
import "../tinymce/plugins/visualblocks/plugin.min"
import "../tinymce/plugins/visualchars/plugin.min"
import "../tinymce/plugins/wordcount/plugin.min"

export default class TinyMceEditor {
  constructor(editorId, form, imagePickerUrl, iconPickerUrl, fontsCssUrls) {
    this.editorId = editorId
    this.form = form
    this.imagePickerUrl = imagePickerUrl
    this.iconPickerUrl = iconPickerUrl
    this.fontsCssUrls = fontsCssUrls || []
    this.initialize()
  }

  initialize() {
    const editorSelector = `#${this.editorId}`
    if(!document.querySelector(editorSelector)) { return false; }

    tinymce.init(this.getConfig())
  }

  getConfig() {
    const swatches = []

    getThemeSwatches().forEach(({ value, label }) => swatches.push(value, label))

    swatches.push(
      '000000', 'Black',
      '993300', 'Burnt orange',
      '333300', 'Dark olive',
      '003300', 'Dark green',
      '003366', 'Dark azure',
      '000080', 'Navy Blue',
      '333399', 'Indigo',
      '333333', 'Very dark gray',
      '800000', 'Maroon',
      'FF6600', 'Orange',
      '808000', 'Olive',
      '008000', 'Green',
      '008080', 'Teal',
      '0000FF', 'Blue',
      '666699', 'Grayish blue',
      '808080', 'Gray',
      'FF0000', 'Red',
      'FF9900', 'Amber',
      '99CC00', 'Yellow green',
      '339966', 'Sea green',
      '33CCCC', 'Turquoise',
      '3366FF', 'Royal blue',
      '800080', 'Purple',
      '999999', 'Medium gray',
      'FF00FF', 'Magenta',
      'FFCC00', 'Gold',
      'FFFF00', 'Yellow',
      '00FF00', 'Lime',
      '00FFFF', 'Aqua',
      '00CCFF', 'Sky blue',
      '993366', 'Red violet',
      'FFFFFF', 'White',
      'FF99CC', 'Pink',
      'FFCC99', 'Peach',
      'FFFF99', 'Light yellow',
      'CCFFCC', 'Pale green',
      'CCFFFF', 'Pale cyan',
      '99CCFF', 'Light sky blue',
      'CC99FF', 'Plum',
    )

    return {
      selector: `#${this.editorId}`,
      browser_spellcheck: true,
      skin_url: '/editor/skins/ui/oxide',
      content_css: ['/editor/skins/ui/oxide/content.min.css', ...this.fontsCssUrls], // fonts defined in EDITOR_FONT_CSS_LOGICAL_PATHS
      font_family_formats: "Inter='Inter', sans-serif; Caveat='Caveat', cursive; Marck Script='Marck Script', cursive; Anton=Anton; Poppins=Poppins; Andale Mono=andale mono, times; Arial=arial, helvetica, sans-serif; Arial Black=arial black, avant garde; Book Antiqua=book antiqua, palatino; Comic Sans MS=comic sans ms, sans-serif; Courier New=courier new, courier; Georgia=georgia, palatino; Helvetica=helvetica; Impact=impact, chicago; Tahoma=tahoma, arial, helvetica, sans-serif; Terminal=terminal, monaco; Times New Roman=times new roman, times; Trebuchet MS=trebuchet ms, geneva; Verdana=verdana, geneva",
      color_default_foreground: '000',
      color_default_background: 'fff',
      color_map: swatches,
      font_size_input_default_unit: 'rem',
      font_size_formats: '0.75rem 0.875rem 1rem 1.125rem 1.25rem 1.5rem 1.75rem 2rem 2.5rem 3rem 3.5rem 4rem 4.5rem ' +
        '5rem 5.5rem 6rem 6.5rem 7rem 7.5rem 8rem 8.5rem 9rem 9.5rem 10rem 10.5rem 11rem 11.5rem 12rem 12.5rem',
      convert_urls: false,
      relative_urls: false,
      remove_script_host: false,
      formats: {
        alignleft: [
          { selector: 'figure,p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,audio,video', styles: { textAlign: 'left' }, inherit: false, defaultBlock: 'div' },
          { selector: 'img,table', collapsed: false, styles: { float: 'left', marginRight: '1.5rem', marginBottom: '0.5rem' } }
        ],
        alignright: [
          { selector: 'figure,p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,audio,video', styles: { textAlign: 'right' }, inherit: false, defaultBlock: 'div' },
          { selector: 'img,table', collapsed: false, styles: { float: 'right', marginLeft: '1.5rem', marginBottom: '0.5rem' } }
        ],
        aligncenter: [
          { selector: 'figure,p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,audio,video', styles: { textAlign: 'center' }, inherit: false, defaultBlock: 'div' },
          { selector: 'img,table', collapsed: false, styles: { display: 'block', marginLeft: 'auto', marginRight: 'auto' } }
        ]
      },
      quickbars_selection_toolbar: 'link alignleft aligncenter alignright forecolor',
      quickbars_insert_toolbar: false,
      quickbars_image_toolbar: 'image',
      image_advtab: true,
      table_toolbar: this.tableToolbar(),
      table_header_type: 'sectionCells',
      plugins: this.plugins(),
      height: 500,
      resize: true,
      min_height: 300,
      max_height: 1000,
      link_class_list: [
        { title: 'None', value: '' },

        {
          title: 'Normal buttons',
          menu: [
            { title: 'Primary',   value: 'btn btn-primary' },
            { title: 'Secondary', value: 'btn btn-secondary' },
            { title: 'Success',   value: 'btn btn-success' },
            { title: 'Danger',    value: 'btn btn-danger' },
            { title: 'Warning',   value: 'btn btn-warning' },
            { title: 'Info',      value: 'btn btn-info' },
            { title: 'Light',     value: 'btn btn-light' },
            { title: 'Dark',      value: 'btn btn-dark' }
          ]
        },

        {
          title: 'Small buttons',
          menu: [
            { title: 'Primary',   value: 'btn btn-sm btn-primary' },
            { title: 'Secondary', value: 'btn btn-sm btn-secondary' },
            { title: 'Success',   value: 'btn btn-sm btn-success' },
            { title: 'Danger',    value: 'btn btn-sm btn-danger' },
            { title: 'Warning',   value: 'btn btn-sm btn-warning' },
            { title: 'Info',      value: 'btn btn-sm btn-info' },
            { title: 'Light',     value: 'btn btn-sm btn-light' },
            { title: 'Dark',      value: 'btn btn-sm btn-dark' }
          ]
        },

        {
          title: 'Large buttons',
          menu: [
            { title: 'Primary',   value: 'btn btn-lg btn-primary' },
            { title: 'Secondary', value: 'btn btn-lg btn-secondary' },
            { title: 'Success',   value: 'btn btn-lg btn-success' },
            { title: 'Danger',    value: 'btn btn-lg btn-danger' },
            { title: 'Warning',   value: 'btn btn-lg btn-warning' },
            { title: 'Info',      value: 'btn btn-lg btn-info' },
            { title: 'Light',     value: 'btn btn-lg btn-light' },
            { title: 'Dark',      value: 'btn btn-lg btn-dark' }
          ]
        },

        {
          title: 'Normal buttons outline',
          menu: [
            { title: 'Primary outline',   value: 'btn btn-outline-primary' },
            { title: 'Secondary outline', value: 'btn btn-outline-secondary' },
            { title: 'Success outline',   value: 'btn btn-outline-success' },
            { title: 'Danger outline',    value: 'btn btn-outline-danger' },
            { title: 'Warning outline',   value: 'btn btn-outline-warning' },
            { title: 'Info outline',      value: 'btn btn-outline-info' },
            { title: 'Light outline',     value: 'btn btn-outline-light' },
            { title: 'Dark outline',      value: 'btn btn-outline-dark' }
          ]
        },

        {
          title: 'Small buttons outline',
          menu: [
            { title: 'Primary outline',   value: 'btn btn-sm btn-outline-primary' },
            { title: 'Secondary outline', value: 'btn btn-sm btn-outline-secondary' },
            { title: 'Success outline',   value: 'btn btn-sm btn-outline-success' },
            { title: 'Danger outline',    value: 'btn btn-sm btn-outline-danger' },
            { title: 'Warning outline',   value: 'btn btn-sm btn-outline-warning' },
            { title: 'Info outline',      value: 'btn btn-sm btn-outline-info' },
            { title: 'Light outline',     value: 'btn btn-sm btn-outline-light' },
            { title: 'Dark outline',      value: 'btn btn-sm btn-outline-dark' }
          ]
        },

        {
          title: 'Large buttons outline',
          menu: [
            { title: 'Primary outline',   value: 'btn btn-lg btn-outline-primary' },
            { title: 'Secondary outline', value: 'btn btn-lg btn-outline-secondary' },
            { title: 'Success outline',   value: 'btn btn-lg btn-outline-success' },
            { title: 'Danger outline',    value: 'btn btn-lg btn-outline-danger' },
            { title: 'Warning outline',   value: 'btn btn-lg btn-outline-warning' },
            { title: 'Info outline',      value: 'btn btn-lg btn-outline-info' },
            { title: 'Light outline',     value: 'btn btn-lg btn-outline-light' },
            { title: 'Dark outline',      value: 'btn btn-lg btn-outline-dark' }
          ]
        }
      ],

      // Toolbar matching all above plugins + core features
      toolbar: [
        'blocks fontfamily fontsize lineheight bold italic underline forecolor backcolor removeformat|' +
        'alignleft aligncenter alignright alignjustify outdent indent numlist bullist hr |' +
        'emoticons insertIcon fullscreen preview pastetext | link my-quickimage image media template | searchreplace remove help'
      ],

      setup: (editor) => {
        let editorChangeHandlerId;
        const form = this.form;

        this.registerIconPicker(editor)

        editor.on('init', function () {
          const tox = document.querySelector(".tox-promotion");
          if (tox) {
            tox.remove();
          }
        });

        // save on change with 5s delay
        editor.on('Paste input Undo Redo', function (e) {
          clearTimeout(editorChangeHandlerId);
          editorChangeHandlerId = setTimeout(function () {
            form.requestSubmit();
          }, 5000)
        });

        editor.addShortcut('ctrl+s', 'Save Content', function() {
          if (form) form.requestSubmit();
          return false;
        });

        editor.ui.registry.addButton('my-quickimage', {
          icon: 'upload',
          tooltip: 'Upload Image',
          onAction: () => {
            window.Turbo.visit(this.imagePickerUrl, { frame: 'remote_modal' })
          }
        });

        // Listen for image selection from the modal
        window.addEventListener('tinymce:imageSelected', (event) => {
          const { url } = event.detail
          editor.insertContent(`<img src="${url}" alt="" />`)
        });

        // Listen for icon selection from the modal
        window.addEventListener('tinymce:iconSelected', (event) => {
          const { name } = event.detail
          editor.insertContent(`<span class="bi bi-${name}" aria-hidden="true">&nbsp;</span>`)
        });
      }
    }
  }

  // Registers the "Insert Icon" toolbar button, which opens a searchable modal
  // (see IconsController#picker) listing all Bootstrap Icons and dispatches
  // tinymce:iconSelected on click (handled above). The inserted tag is a
  // <span>, not an <i>: TinyMCE's "italic" format treats <i> as a fallback
  // match for its own em-producing command and silently rewrites any <i> it
  // parses into <em> on save. It also carries &nbsp; as content because
  // TinyMCE strips empty inline elements on save; .icon-feature-list (see
  // app/assets/stylesheets/_custom_classes.scss) hides that content and
  // shows only the glyph.
  registerIconPicker(editor) {
    editor.ui.registry.addButton('insertIcon', {
      icon: 'gallery',
      tooltip: 'Insert Icon',
      onAction: () => {
        window.Turbo.visit(this.iconPickerUrl, { frame: 'remote_modal' })
      }
    })
  }

  plugins() {
    return [
      'advlist',      // Advanced list options (dashes, greek, etc.)
      // 'anchor',       // Insert anchors
      'autolink',     // Auto-convert URLs to links (no button)
      // 'autoresize',   // Auto-resize editor height (no button) - disabled to allow manual resize
      'code',         // View HTML source
      'codesample',   // Code snippets with syntax highlighting
      'emoticons',    // Emojis
      'fullscreen',   // Fullscreen mode
      'help',         // Help dialog
      'image',        // Insert/upload images
      'importcss',    // Import CSS classes (no button)
      // 'insertdatetime', // Date/Time
      'link',         // Hyperlinks
      'lists',        // Standard bullet/numbered lists
      'media',        // HTML5 Video/Audio
      'nonbreaking',  // Non-breaking space (&nbsp;)
      'preview',      // Preview content
      'quickbars',    // Contextual floating toolbar (no main toolbar button)
      // 'save',         // Save button (submits form)
      'searchreplace', // Find and replace
      'table',        // Table editor
      'wordcount'     // Word count (footer only)
    ]
  }

  tableToolbar() {
    return 'tableprops '
      + 'tabledelete '
      + 'tablecellprops '
      + 'tablerowprops '
      + 'tablerowheader '
      + 'tablecolheader '
      // + 'tablemergecells '
      // + 'tablesplitcells '
      + 'tableinsertrowbefore '
      + 'tableinsertrowafter '
      + 'tabledeleterow '
      + 'tableinsertcolbefore '
      + 'tableinsertcolafter '
      + 'tabledeletecol '
      // + 'tablecutrow '
      // + 'tablecopyrow '
      // + 'tablepasterowbefore '
      // + 'tablepasterowafter '
      // + 'tablecutcol '
      // + 'tablecopycol '
      // + 'tablepastecolbefore '
      // + 'tablepastecolafter '
      // + 'tableinsertdialog '
      // + 'tablecellvalign '
      // + 'tablecellborderwidth '
      // + 'tablecellborderstyle '
      // + 'tablecellbackgroundcolor '
      // + 'tablecellbordercolor '
      // + 'tablecaption '
      ;
  }

  destroy() {
    const editor = tinymce.get(this.editorId)
    const sink = document.querySelector('.tox-tinymce-aux')

    if (editor) {
      editor.remove()
    }

    if (sink) {
      sink.remove()
    }
  }
}
